import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/app/Layout";
import { formatINR, statusTone } from "@/lib/format";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Copy, Plus, X, Pencil } from "lucide-react";
import MultiSelectFilter from "@/components/app/MultiSelectFilter";

const uniq = (arr) => Array.from(new Set(arr.map((s) => (s || "").trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b));

export default function Freelancers() {
  const { user } = useAuth();
  const canInviteFreelancers = ["SuperAdmin", "TeamLeader"].includes(user.role);
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState([]);
  const [expertiseFilter, setExpertiseFilter] = useState([]);
  const [skillsFilter, setSkillsFilter] = useState([]);
  const [params, setParams] = useSearchParams();
  const [inviteOpen, setInviteOpen] = useState(params.get("invite") === "1");
  const [invitePayload, setInvitePayload] = useState({ name: "", email: "", phone: "", category: "General", skills: "", department: "", expertise: "" });
  const [lastInviteLink, setLastInviteLink] = useState("");
  const [seats, setSeats] = useState(null);
  const [categories, setCategories] = useState([]);
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const [newCat, setNewCat] = useState("");
  const [editing, setEditing] = useState(null);
  const [smes, setSmes] = useState([]);
  const [smeForm, setSmeForm] = useState({ name: "", expertise: "" });
  const [smeEditing, setSmeEditing] = useState(null);

  const openEdit = (f) => setEditing({
    id: f.id, name: f.name || "", phone: f.phone || "", email: f.email || "",
    category: f.category || "General", department: f.department || "", expertise: f.expertise || "",
    skills: (f.skills || []).join(", "), status: f.status, rating: f.rating || 0,
  });

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/freelancers/${editing.id}`, {
        name: editing.name, phone: editing.phone, category: editing.category,
        department: editing.department, expertise: editing.expertise,
        skills: editing.skills.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Freelancer updated");
      setEditing(null); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const load = async () => {
    const q = new URLSearchParams();
    if (statusFilter) q.set("status", statusFilter);
    if (categoryFilter) q.set("category", categoryFilter);
    const { data } = await api.get(`/freelancers?${q.toString()}`);
    setItems(data);
  };
  const loadSeats = async () => { try { const { data } = await api.get("/seats"); setSeats(data); } catch { setSeats(null); } };
  const loadCategories = async () => { try { const { data } = await api.get("/categories"); setCategories(data); } catch { setCategories([]); } };

  useEffect(() => { load(); }, [statusFilter, categoryFilter]);
  useEffect(() => { loadSeats(); loadCategories(); }, []);

  const addCategory = async () => {
    const name = newCat.trim();
    if (!name) return;
    try { const { data } = await api.post("/categories", { name }); setCategories((prev) => [...prev, data].sort((a, b) => a.name.localeCompare(b.name))); setNewCat(""); toast.success("Category added"); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const removeCategory = async (c) => {
    if (!window.confirm(`Delete category "${c.name}"?`)) return;
    try { await api.delete(`/categories/${c.id}`); setCategories((prev) => prev.filter((x) => x.id !== c.id)); toast.success("Category deleted"); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  // SME master — Super Admins & Operation Heads manage; Managers pick from this list on projects.
  const loadSmes = async () => { try { const { data } = await api.get("/smes"); setSmes(data); } catch {} };
  useEffect(() => { if (canInviteFreelancers) loadSmes(); }, [canInviteFreelancers]);
  const submitSme = async (e) => {
    e.preventDefault();
    try {
      if (smeEditing) await api.patch(`/smes/${smeEditing}`, smeForm);
      else await api.post("/smes", smeForm);
      setSmeForm({ name: "", expertise: "" }); setSmeEditing(null); loadSmes();
      toast.success("SME saved");
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const editSme = (s) => { setSmeForm({ name: s.name, expertise: s.expertise || "" }); setSmeEditing(s.id); };
  const removeSme = async (s) => {
    if (!window.confirm(`Delete SME ${s.name}?`)) return;
    try { await api.delete(`/smes/${s.id}`); loadSmes(); } catch (err) { toast.error(formatApiError(err)); }
  };

  const canInvite = true;
  const blockReason = "";

  const anyFilter = !!(statusFilter || categoryFilter || departmentFilter.length || expertiseFilter.length || skillsFilter.length);
  const clearAll = () => { setStatusFilter(""); setCategoryFilter(""); setDepartmentFilter([]); setExpertiseFilter([]); setSkillsFilter([]); };
  const shown = items.filter((f) =>
    (!departmentFilter.length || departmentFilter.includes((f.department || "").trim())) &&
    (!expertiseFilter.length || expertiseFilter.includes((f.expertise || "").trim())) &&
    (!skillsFilter.length || (f.skills || []).some((s) => skillsFilter.includes((s || "").trim()))));

  const submitInvite = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/freelancers/invite", {
        name: invitePayload.name,
        email: invitePayload.email,
        phone: invitePayload.phone,
        category: invitePayload.category,
        department: invitePayload.department,
        expertise: invitePayload.expertise,
        skills: invitePayload.skills.split(",").map((s) => s.trim()).filter(Boolean),
      });
      const link = window.location.origin + data.invite_link;
      setLastInviteLink(link);
      toast.success("Freelancer invited. Copy invite link below.");
      setInvitePayload({ name: "", email: "", phone: "", category: "General", skills: "", department: "", expertise: "" });
      load(); loadSeats();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div>
      <PageHeader
        title="Freelancers"
        description="Every freelancer in your studio."
        actions={
          canInviteFreelancers && (
            <button
              data-testid="invite-freelancer-btn"
              disabled={!canInvite}
              title={blockReason}
              onClick={() => { if (canInvite) { setInviteOpen(true); setParams({}); } else if (blockReason) toast.error(blockReason); }}
              className={`px-4 py-2 text-sm hard-shadow-hover ${canInvite ? "bg-black text-white" : "bg-neutral-300 text-neutral-600 cursor-not-allowed"}`}>
              + Invite Freelancer
            </button>
          )
        }
      />

      <div className="p-8">
        {seats && (
          <div data-testid="freelancer-seats-card" className="mb-4 flex flex-wrap items-center gap-3 bg-white border border-black/10 rounded-2xl p-4">
            <div className="label-caps text-royal-800">Freelancers</div>
            <div className="font-mono-tight text-sm">
              <span className="font-semibold">{seats.used.freelancer}</span>
              <span className="text-neutral-500"> active · User Limit: </span>
              <span className="font-semibold text-royal-700" data-testid="freelancer-user-limit">Unlimited</span>
            </div>
          </div>
        )}
        <div className="flex flex-wrap gap-3 mb-4">
          <select data-testid="filter-status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="border border-black rounded-none px-3 py-2 text-sm bg-white">
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="invited">Invited</option>
            <option value="deactivated">Deactivated</option>
          </select>
          <select data-testid="filter-category" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="border border-black rounded-none px-3 py-2 text-sm bg-white">
            <option value="">All Categories</option>
            {categories.map((c) => <option key={c.id}>{c.name}</option>)}
          </select>
          <MultiSelectFilter testid="filter-expertise" label="Expertise" options={uniq(items.map((f) => f.expertise))} selected={expertiseFilter} onChange={setExpertiseFilter} />
          <MultiSelectFilter testid="filter-department" label="Department" options={uniq(items.map((f) => f.department))} selected={departmentFilter} onChange={setDepartmentFilter} />
          <MultiSelectFilter testid="filter-skills" label="Skills" searchable options={uniq(items.flatMap((f) => f.skills || []))} selected={skillsFilter} onChange={setSkillsFilter} />
          {anyFilter && (
            <button data-testid="filters-clear-all" onClick={clearAll} className="inline-flex items-center gap-1 text-sm underline underline-offset-4"><X size={14} /> Clear All</button>
          )}
          <div className="ml-auto self-center text-xs text-neutral-500 font-mono-tight" data-testid="freelancers-count">{shown.length} / {items.length}</div>
        </div>

        <div className="border border-neutral-200 bg-white overflow-x-auto">
          <table className="fms-table w-full" data-testid="freelancers-table">
            <thead><tr><th>Name</th><th>Category</th><th>Expertise</th><th>Department</th><th>Skills</th><th>Rating</th><th>Status</th><th>Availability</th><th>Projects Linked</th><th></th></tr></thead>
            <tbody>
              {shown.map((f) => (
                <tr key={f.id} data-testid={`freelancer-row-${f.id}`}>
                  <td>
                    <div className="font-medium">{f.name}</div>
                    <div className="text-xs text-neutral-500">{f.email}</div>
                  </td>
                  <td className="font-mono-tight text-xs">{f.category}</td>
                  <td className="text-xs text-neutral-600" data-testid={`freelancer-expertise-${f.id}`}>{f.expertise || "—"}</td>
                  <td className="text-xs text-neutral-600" data-testid={`freelancer-dept-${f.id}`}>{f.department || "—"}</td>
                  <td className="text-xs text-neutral-600">{(f.skills || []).slice(0, 3).join(", ")}</td>
                  <td className="font-mono-tight">{f.rating?.toFixed?.(1) ?? f.rating ?? "—"}</td>
                  <td><span className={`px-2 py-0.5 text-xs font-mono-tight uppercase ${statusTone(f.status)}`}>{f.status}</span></td>
                  <td className="text-xs font-mono-tight text-neutral-600">{f.availability_status || "—"}</td>
                  <td className="font-mono-tight text-sm" data-testid={`freelancer-projects-linked-${f.id}`}>{f.projects_linked ?? 0}</td>
                  <td className="whitespace-nowrap space-x-3">
                    <Link data-testid={`view-freelancer-${f.id}`} to={`/freelancers/${f.id}`} className="text-sm underline underline-offset-4">View</Link>
                    {canInviteFreelancers && <button data-testid={`edit-freelancer-${f.id}`} onClick={() => openEdit(f)} className="text-sm underline underline-offset-4 inline-flex items-center gap-1"><Pencil size={12} /> Edit</button>}
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={10} className="text-center text-neutral-500 py-8">No freelancers yet.</td></tr>}
              {items.length > 0 && shown.length === 0 && <tr><td colSpan={10} className="text-center text-neutral-500 py-8">No freelancers match your filters.</td></tr>}
            </tbody>
          </table>
        </div>

        {canInviteFreelancers && (
          <div className="mt-6 bg-white border border-black/10 rounded-2xl p-5" data-testid="freelancers-smes-admin">
            <div className="label-caps text-royal-800 mb-2">SME Master — Subject Matter Experts</div>
            <form onSubmit={submitSme} className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-3">
              <Input data-testid="sme-name" required placeholder="SME Name" value={smeForm.name} onChange={(e) => setSmeForm({ ...smeForm, name: e.target.value })} className="rounded-lg border-black/15 h-10" />
              <Input data-testid="sme-expertise" placeholder="Expertise" value={smeForm.expertise} onChange={(e) => setSmeForm({ ...smeForm, expertise: e.target.value })} className="rounded-lg border-black/15 h-10 md:col-span-2" />
              <button data-testid="sme-submit" className="btn-primary">{smeEditing ? "Save SME" : "Add SME"}</button>
            </form>
            <ul className="space-y-1" data-testid="smes-list">
              {smes.map((s) => (
                <li key={s.id} className="flex items-center justify-between border border-black/10 rounded-lg px-3 py-2 text-sm" data-testid={`sme-row-${s.id}`}>
                  <div><b>{s.name}</b> <span className="text-xs text-neutral-500">· {s.expertise || "—"}</span></div>
                  <div className="space-x-3">
                    <button data-testid={`sme-edit-${s.id}`} onClick={() => editSme(s)} className="text-xs underline underline-offset-4">Edit</button>
                    <button data-testid={`sme-del-${s.id}`} onClick={() => removeSme(s)} className="text-xs underline underline-offset-4 text-red-600">Delete</button>
                  </div>
                </li>
              ))}
              {smes.length === 0 && <li className="text-neutral-500 text-sm text-center py-2">No SMEs yet.</li>}
            </ul>
          </div>
        )}
      </div>

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="rounded-none border-2 border-black" data-testid="invite-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Invite Freelancer</DialogTitle></DialogHeader>
          <form onSubmit={submitInvite} className="space-y-3">
            <div><label className="label-caps block mb-1">Name</label><Input data-testid="invite-name" required value={invitePayload.name} onChange={(e) => setInvitePayload({ ...invitePayload, name: e.target.value })} className="rounded-none border-black" /></div>
            <div><label className="label-caps block mb-1">Email</label><Input data-testid="invite-email" required type="email" value={invitePayload.email} onChange={(e) => setInvitePayload({ ...invitePayload, email: e.target.value })} className="rounded-none border-black" /></div>
            <div><label className="label-caps block mb-1">Phone</label><Input data-testid="invite-phone" value={invitePayload.phone} onChange={(e) => setInvitePayload({ ...invitePayload, phone: e.target.value })} className="rounded-none border-black" /></div>
            <div><label className="label-caps block mb-1 flex items-center justify-between">Category
              <button type="button" data-testid="manage-categories-btn" onClick={() => setCategoriesOpen(true)} className="text-[10px] underline underline-offset-4 normal-case tracking-normal">Manage</button>
            </label>
              <select data-testid="invite-category" value={invitePayload.category} onChange={(e) => setInvitePayload({ ...invitePayload, category: e.target.value })} className="w-full border border-black rounded-none px-3 py-2 text-sm bg-white">
                {categories.map((c) => <option key={c.id}>{c.name}</option>)}
                {categories.length === 0 && <option>General</option>}
              </select>
            </div>
            <div><label className="label-caps block mb-1">Skills (comma separated)</label><Input data-testid="invite-skills" value={invitePayload.skills} onChange={(e) => setInvitePayload({ ...invitePayload, skills: e.target.value })} className="rounded-none border-black" /></div>
            <div><label className="label-caps block mb-1">Department</label><Input data-testid="invite-department" value={invitePayload.department} onChange={(e) => setInvitePayload({ ...invitePayload, department: e.target.value })} className="rounded-none border-black" placeholder="e.g. Publication" /></div>
            <div><label className="label-caps block mb-1">Expertise</label><Input data-testid="invite-expertise" value={invitePayload.expertise} onChange={(e) => setInvitePayload({ ...invitePayload, expertise: e.target.value })} className="rounded-none border-black" placeholder="e.g. Oncology" /></div>
            <DialogFooter><button data-testid="invite-submit" type="submit" className="bg-black text-white px-4 py-2 text-sm">Send Invite</button></DialogFooter>
          </form>
          {lastInviteLink && (
            <div className="mt-2 border-2 border-black p-3 bg-[#D4FF00]" data-testid="invite-link-box">
              <div className="label-caps mb-1">Invite Link (share manually)</div>
              <div className="flex items-center gap-2">
                <code className="text-xs break-all flex-1 font-mono-tight">{lastInviteLink}</code>
                <button data-testid="copy-invite-link" onClick={() => { navigator.clipboard.writeText(lastInviteLink); toast.success("Copied"); }} className="border border-black bg-white p-1.5"><Copy size={14} /></button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={categoriesOpen} onOpenChange={setCategoriesOpen}>
        <DialogContent className="rounded-[1.25rem]" data-testid="categories-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Manage Categories</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              <Input data-testid="new-category-input" value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="e.g. Motion Design" className="rounded-lg border-black/15" />
              <button data-testid="add-category-btn" onClick={addCategory} className="btn-primary inline-flex items-center gap-1"><Plus size={14} /> Add</button>
            </div>
            <ul className="border border-black/10 rounded-lg divide-y divide-black/5 max-h-72 overflow-auto" data-testid="categories-list">
              {categories.map((c) => (
                <li key={c.id} className="flex justify-between items-center px-3 py-2 text-sm" data-testid={`category-row-${c.id}`}>
                  <span>{c.name}</span>
                  <button data-testid={`delete-category-${c.id}`} onClick={() => removeCategory(c)} className="text-[#B02A00] hover:bg-[#B02A00]/10 rounded p-1"><X size={14} /></button>
                </li>
              ))}
              {categories.length === 0 && <li className="px-3 py-4 text-center text-neutral-500 text-sm">No categories yet — add one above.</li>}
            </ul>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="rounded-[1.25rem] max-w-md" data-testid="edit-freelancer-dialog">
          {editing && (
            <>
              <DialogHeader><DialogTitle className="font-display text-2xl">Edit freelancer</DialogTitle></DialogHeader>
              <form onSubmit={saveEdit} className="space-y-3">
                <div><label className="label-caps block mb-1">Name</label><Input data-testid="edit-fl-name" required value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="rounded-lg border-black/15 h-10" /></div>
                <div><label className="label-caps block mb-1">Email (read-only)</label><Input data-testid="edit-fl-email" value={editing.email} disabled className="rounded-lg border-black/15 h-10 opacity-60" /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div><label className="label-caps block mb-1">Phone</label><Input data-testid="edit-fl-phone" value={editing.phone} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} className="rounded-lg border-black/15 h-10" /></div>
                  <div><label className="label-caps block mb-1">Category</label>
                    <select data-testid="edit-fl-category" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="w-full border border-black/15 rounded-lg px-2 h-10 bg-white text-sm">
                      {categories.map((c) => <option key={c.id}>{c.name}</option>)}
                      <option value="General">General</option>
                    </select>
                  </div>
                </div>
                <div><label className="label-caps block mb-1">Department</label><Input data-testid="edit-fl-department" value={editing.department} onChange={(e) => setEditing({ ...editing, department: e.target.value })} className="rounded-lg border-black/15 h-10" /></div>
                <div><label className="label-caps block mb-1">Expertise</label><Input data-testid="edit-fl-expertise" value={editing.expertise} onChange={(e) => setEditing({ ...editing, expertise: e.target.value })} className="rounded-lg border-black/15 h-10" /></div>
                <div><label className="label-caps block mb-1">Skills (comma separated)</label><Input data-testid="edit-fl-skills" value={editing.skills} onChange={(e) => setEditing({ ...editing, skills: e.target.value })} className="rounded-lg border-black/15 h-10" /></div>
                <DialogFooter><button data-testid="save-fl-edit" type="submit" className="btn-primary">Save changes</button></DialogFooter>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
